/**
 * Approve a multi-company run (gaps plan §7, extended to a batch).
 *
 * Same contract as the single-company dialog and for the same reason: the
 * estimate is a **lower bound**, and the documents that cost the most are the
 * ones it cannot price at all. A batch makes both worse — thirteen companies
 * is ~24M planned tokens plus ~27M unpriced ones — so the two halves are
 * shown separately rather than added into one confident-looking figure.
 */
import { useQuery } from '@tanstack/react-query'
import type { BatchEstimate } from '@server/jobs/estimate.ts'
import { ApiError, api } from '~/lib/api.ts'
import { useT } from '~/i18n/index.tsx'
import { count } from '~/i18n/format.ts'
import { Button, Label, Skeleton } from '~/components/ui/primitives.tsx'
import { Dialog } from '~/components/ui/overlay.tsx'
import { ErrorState } from '~/components/ui/empty.tsx'

export function BatchDialog({
  symbols, open, enabled, pending, maxTokens, onClose, onConfirm,
}: {
  symbols: string[]
  open: boolean
  enabled: boolean
  pending: boolean
  /** The server's ceiling, so the dialog refuses before the round trip. */
  maxTokens: number
  onClose: () => void
  onConfirm: () => void
}) {
  const { t, locale } = useT()

  const { data, error } = useQuery({
    queryKey: ['batch-estimate', symbols.join(',')],
    queryFn: () =>
      api<{ estimate: BatchEstimate }>('/api/jobs/batch/estimate', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ kind: 'fill_company', symbols }),
      }),
    enabled: open && symbols.length > 0,
    staleTime: 60_000,
  })

  const e = data?.estimate
  const n = (x: number) => count(x, locale)
  // A dialog that shows a total and then a live Run button the server will
  // reject is worse than one that says so up front.
  const overCap = Boolean(e) && maxTokens > 0 && e!.estTokens > maxTokens

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('batch.title')}
      closeLabel={t('action.close')}
      width={680}
      footer={
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={onClose} variant="ghost">{t('estimate.cancel')}</Button>
          <div className="flex-1" />
          <Button
            variant="primary"
            disabled={!enabled || !e || pending || overCap}
            onClick={onConfirm}
          >
            {t('estimate.confirm')}
          </Button>
        </div>
      }
    >
      {error ? (
        <ErrorState title={t('common.error')} detail={(error as ApiError).message} />
      ) : !e ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
            <Figure label={t('batch.companies')} value={n(e.companies)} />
            <Figure label={t('batch.plannedDocs')} value={n(e.documents)} />
            <Figure label={t('estimate.tokens')} value={n(e.estTokens)} />
            <Figure
              label={t('estimate.cost')}
              value={e.costUsd === null ? t('estimate.noCost') : `$${e.costUsd.toFixed(2)}`}
              muted={e.costUsd === null}
            />
          </div>

          {overCap && (
            <p className="rounded-[var(--radius-control)] border border-[var(--fail)]/30 bg-[var(--fail-bg)] px-2.5 py-1.5 text-[length:var(--text-data)] leading-[var(--text-data--line-height)] text-[var(--fail)]">
              {t('corpus.gaps.overCapFull', { n: n(e.estTokens), cap: n(maxTokens) })}
            </p>
          )}

          {/* The half that cannot be priced, named rather than folded in. */}
          {e.unplannedDocuments > 0 && (
            <p className="rounded-[var(--radius-control)] border border-[var(--warn)]/30 bg-[var(--warn-bg)] px-2.5 py-1.5 text-[length:var(--text-data)] leading-[var(--text-data--line-height)] text-[var(--text)]">
              <span className="font-medium">
                {t('batch.unplanned')}: {n(e.unplannedDocuments)}.{' '}
              </span>
              {t('batch.unplannedNote', { n: n(e.unplannedTokensGuess ?? 0) })}
            </p>
          )}

          {e.calibration.factor !== null && (
            <p className="max-w-[74ch] text-[length:var(--text-data)] leading-[var(--text-data--line-height)] text-[var(--text-muted)]">
              <span className="font-medium text-[var(--text)]">{t('estimate.lowerBound')}. </span>
              {t('estimate.calibration', {
                docs: n(e.calibration.sampleDocuments),
                factor: e.calibration.factor.toFixed(2),
                budget: (1 / e.calibration.factor).toFixed(2),
              })}
            </p>
          )}

          <p className="text-[length:var(--text-data)] text-[var(--text-muted)]">
            {t('corpus.gaps.queueNote')}
          </p>

          {/* Per company, so a total that looks wrong can be traced to a row. */}
          <div className="max-h-48 overflow-auto rounded-[var(--radius-control)] border border-[var(--border)]"
               style={{ overscrollBehavior: 'contain' }}>
            <table className="w-full text-[length:var(--text-micro)]">
              <tbody>
                {e.perCompany.map((p) => (
                  <tr key={p.symbol} className="border-b border-[var(--border)] last:border-0">
                    <td className="px-2 py-1 font-[family-name:var(--font-mono)] text-[var(--text-muted)]">
                      {p.symbol}
                    </td>
                    <td className="tnum px-2 py-1 text-end text-[var(--text-muted)]">
                      {n(p.documents)}
                    </td>
                    <td className="tnum px-2 py-1 text-end text-[var(--text)]">
                      {p.estTokens === null ? '—' : n(p.estTokens)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!enabled && (
            <p className="text-[length:var(--text-data)] text-[var(--text-muted)]">
              {t('job.disabledWhy')}
            </p>
          )}
        </div>
      )}
    </Dialog>
  )
}

function Figure({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className={'tnum mt-0.5 text-[length:var(--text-title)] font-medium ' +
        (muted ? 'text-[var(--text-faint)]' : 'text-[var(--text)]')}>
        {value}
      </div>
    </div>
  )
}
